import { createSwitchlessReducer } from '../../util/switchlessReducer';
import namespace from './namespace';
import type { Dispatch } from 'redux';
import { ReportMetadata, ReportMetadataResponse } from '../../types/reports';
import fulfill from '../../fulfill';
import { CORE_API_HOSTNAME } from '../../env';
import { paramsEqual } from '../../helpers/urlParams';
import { orderedUniqueList } from '../../helpers/paging';
import { parseISOStrict } from '../../helpers/date';
import startOfYear from 'date-fns/startOfYear';
import startOfMonth from 'date-fns/startOfMonth';

const PAGING_LIMIT = 100;

export type pagingInterval = 'month' | 'year' | undefined;

export interface State {
    reports: ReportMetadata[];
    isLoading: boolean;
    starting_after: string | undefined;
    params: URLSearchParams;
    account_id: string;
    pagingCutoff: Date | undefined;
}

export const initialState: State = {
    reports: [],
    isLoading: true,
    starting_after: undefined,
    params: new URLSearchParams({ limit: PAGING_LIMIT.toString() }),
    account_id: '',
    pagingCutoff: undefined,
};

type Requested = {
    params: URLSearchParams;
    account_id: string;
};
type Returned = Requested &
    ReportMetadataResponse & {
        pagingCutoff: Date | undefined;
    };

const getPagingInterval = (interval: pagingInterval, reports: ReportMetadata[]) => {
    // If the first PAGING_LIMIT results are all from the same month, we page by month
    // otherwise we page by year. Worst case for year should not be more than
    // 12 consecutive requests. Worst case for a month is unknown.
    if (interval) {
        // if paging interval is set, use existing value
        return interval;
    }
    if (reports.length < PAGING_LIMIT) {
        return 'year';
    }
    const first = reports[0];
    const last = reports[reports.length - 1];
    if (first.data_from === last.data_from) {
        return 'month';
    }
    return 'year';
};

const shouldFetchMore = (interval: pagingInterval, reports: ReportMetadata[]) => {
    if (reports.length < PAGING_LIMIT) {
        return false;
    }
    const firstFrom = reports[0].data_from || '';
    const lastFrom = reports[reports.length - 1].data_from || '';
    const sameYear = firstFrom.substr(0, 4) === lastFrom.substr(0, 4);
    if (!sameYear) {
        return false;
    }
    const sameMonth = firstFrom.substr(5, 2) === lastFrom.substr(5, 2);
    if (interval === 'month' && !sameMonth) {
        return false;
    }
    return true;
};

const getPagingCutoff = (interval: pagingInterval, reports: ReportMetadata[]) => {
    if (reports.length < PAGING_LIMIT) {
        return undefined;
    }
    const firstFrom = parseISOStrict(reports[0].data_from || '');
    if (!firstFrom || !interval) {
        return undefined;
    }
    if (interval === 'month') {
        return startOfMonth(firstFrom);
    }
    return startOfYear(firstFrom);
};

export const { reducer, actions } = createSwitchlessReducer({
    namespace: namespace,
    initialState,
    reducers: {
        startLoading: (state: State, { account_id, params }: Requested) => {
            const clearList = shouldClearList({ account_id, params }, state);
            if (clearList) {
                return {
                    ...initialState,
                    account_id,
                };
            }
            return {
                ...initialState,
                reports: state.reports,
                pagingCutoff: state.pagingCutoff,
                account_id,
                params,
            };
        },
        addReports: (state: State, { account_id, reports, starting_after, pagingCutoff }: Returned): State => {
            if (account_id !== state.account_id) {
                // disregard old response for wrong account or old query
                return state;
            }
            return {
                ...state,
                isLoading: false,
                starting_after,
                reports: orderedUniqueList(reports || [], state.reports),
                pagingCutoff,
            };
        },
        updateParams: (state: State, { params }: { params: URLSearchParams }): State => {
            return {
                ...state,
                params,
            };
        },
        updatePagingCutoff: (state: State, { pagingCutoff }: { pagingCutoff: Date | undefined }): State => {
            return {
                ...state,
                pagingCutoff,
            };
        },
    },
});

const shouldClearList = (action: Requested, state: State) => {
    if (action.account_id !== state.account_id) {
        return true;
    }
    const blacklist = ['starting_after', 'limit'];
    return !paramsEqual(action.params, state.params, blacklist);
};

const fetchAllReportsFromIntervalRecursively = async (
    account_id: string,
    params: URLSearchParams,
    prevReports: ReportMetadata[],
    prevPagingInterval: pagingInterval
): Promise<Returned> => {
    const response: ReportMetadataResponse = await fulfill.get({
        accountId: account_id,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${account_id}/reports/metadata?${params}`,
        handlers: {
            200: ({ reports, starting_after }: ReportMetadataResponse) => ({ reports, starting_after }),
            400: () => ({ reports: [], starting_after: undefined }),
            404: () => ({ reports: [], starting_after: undefined }),
            500: () => ({ reports: [], starting_after: undefined }), // might happen if starting_after is somehow out of sync with account_id
        },
    });
    const reports = [...prevReports, ...(response.reports || [])];
    const pagingInterval = getPagingInterval(prevPagingInterval, reports);
    const hasMoreReports = shouldFetchMore(pagingInterval, reports);
    const starting_after = response.starting_after;
    if (hasMoreReports && response.starting_after) {
        const nextParams = new URLSearchParams(params);
        nextParams.delete('starting_after');
        nextParams.append('starting_after', response.starting_after);
        return await fetchAllReportsFromIntervalRecursively(account_id, nextParams, reports, pagingInterval);
    }
    const pagingCutoff = getPagingCutoff(pagingInterval, reports);
    return {
        account_id,
        params,
        reports,
        starting_after,
        pagingCutoff,
    };
};

export const createConnectedActions = (dispatch: Dispatch) => {
    const fetchReports = async (account_id: string, params: URLSearchParams) => {
        dispatch(actions.startLoading({ account_id, params }));
        const result = await fetchAllReportsFromIntervalRecursively(account_id, params, [], undefined);
        dispatch(actions.addReports(result));
    };
    const updateParams = async (params: URLSearchParams) => {
        dispatch(actions.updateParams({ params }));
    };
    const updatePagingCutoff = async (pagingCutoff: Date | undefined) => {
        dispatch(actions.updatePagingCutoff({ pagingCutoff }));
    };
    return {
        fetchReports,
        updateParams,
        updatePagingCutoff,
    };
};
